import React, { useEffect } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { getTerminationReasons } from '../../../redux/features/CRDslice';
import {
  ChangeSingleStudentStatus,
  getBatchAdmissionRegister,
  getcroBatchesForNonAssignedLeads,
} from '../../../redux/features/batchSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

const studentStatusOptions = [
  { id: 1, name: 'Inactive' },
  { id: 2, name: 'Temporary Inactive' },
];

const InactiveStudentModal = ({ rowData, dispatchParams, open, setOpen }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, croBatchesForNonAssignedLeads } = useSelector((state) => state.batch);

  const { terminationReasons } = useSelector((state) => state.crd);

  useEffect(() => {
    if (open) {
      dispatch(getTerminationReasons());
      dispatch(getcroBatchesForNonAssignedLeads());
    }
  }, [open, dispatch]);

  const initialValues = {
    course_enrollment_id: rowData?.course_enrollment_id || '',
    batch_assignment_id: rowData?.batch_assignment_id || '',
    course_enrollment_status: rowData?.course_enrollment_status ?? '',
    course_inactive_reason: '',
    course_inactive_remarks: '',
    course_restart_date: null,
    new_assigned_batch: '',
  };

  const ValidationSchema = Yup.object().shape({
    course_enrollment_status: Yup.string().required('Please select status'),
    course_inactive_reason: Yup.string().when('course_enrollment_status', {
      is: (val) => [0, 2].includes(Number(val)),
      then: (schema) => schema.required('Please select reason'),
      otherwise: (schema) => schema.nullable(),
    }),
    course_inactive_remarks: Yup.string().required('Please enter remarks'),

    course_restart_date: Yup.string()
      .nullable()
      .when('course_enrollment_status', {
        is: (val) => Number(val) === 2,
        then: (schema) => schema.required('Please select restart date'),
        otherwise: (schema) => schema.nullable(),
      }),

    new_assigned_batch: Yup.string()
      .nullable()
      .when('course_enrollment_status', {
        is: (val) => Number(val) === 1,
        then: (schema) => schema.required('Please select batch'),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: initialValues,

    validationSchema: ValidationSchema,

    onSubmit: async (values) => {
      const payload = {
        course_enrollment_id: values.course_enrollment_id,
        batch_assignment_id: values.batch_assignment_id,
        course_enrollment_status: Number(values.course_enrollment_status),
        course_inactive_reason: Number(values.course_inactive_reason),
        course_inactive_remarks: values.course_inactive_remarks,
        course_restart_date: Number(values.course_enrollment_status) === 2 ? values.course_restart_date : null,
        new_assigned_batch: Number(values.course_enrollment_status) === 1 ? Number(values.new_assigned_batch) : null,
      };

      dispatch(ChangeSingleStudentStatus(payload)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });

          dispatch(getBatchAdmissionRegister(dispatchParams));

          setOpen(false);
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload?.message || 'Something went wrong', {
            variant: 'error',
          });
        }
      });
    },
  });

  return (
    <GlobalDialog
      title="Change Student Status"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Box>
              <Typography sx={{ mb: 2 }}>Change Course Enrollment to Temporary Inactive</Typography>

              {/* <Typography sx={{ mb: 2 }}>
                Current Status : <strong>{rowData?.studentStatus}</strong>
              </Typography> */}

              <Stack spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Course Enrollment Status"
                  name="course_enrollment_status"
                  value={formik.values.course_enrollment_status}
                  onChange={formik.handleChange}
                  error={formik.touched.course_enrollment_status && Boolean(formik.errors.course_enrollment_status)}
                  helperText={formik.touched.course_enrollment_status && formik.errors.course_enrollment_status}
                >
                  <MenuItem value={0}>Inactive</MenuItem>
                  <MenuItem value={1}>Active</MenuItem>

                  <MenuItem value={2}>Temporary Inactive</MenuItem>
                </TextField>
                <Box>
                  {[0, 2].includes(Number(formik.values.course_enrollment_status)) && (
                    <TextField
                      select
                      fullWidth
                      label="Reason"
                      name="course_inactive_reason"
                      value={formik.values.course_inactive_reason}
                      onChange={formik.handleChange}
                      error={formik.touched.course_inactive_reason && Boolean(formik.errors.course_inactive_reason)}
                      helperText={formik.touched.course_inactive_reason && formik.errors.course_inactive_reason}
                    >
                      {terminationReasons?.map((item) => (
                        <MenuItem key={item.course_inactive_reason_id} value={item.course_inactive_reason_id}>
                          {item.course_inactive_reason_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
                <Box>
                  {Number(formik.values.course_enrollment_status) === 2 && (
                    <TextField
                      fullWidth
                      type="date"
                      label="Course Restart Date"
                      name="course_restart_date"
                      value={formik.values.course_restart_date || ''}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={formik.touched.course_restart_date && Boolean(formik.errors.course_restart_date)}
                      helperText={formik.touched.course_restart_date && formik.errors.course_restart_date}
                    />
                  )}
                </Box>
                <Box>
                  {Number(formik.values.course_enrollment_status) === 1 && (
                    <Autocomplete
                      options={croBatchesForNonAssignedLeads || []}
                      getOptionLabel={(option) => option?.course_batch_name || ''}
                      value={
                        croBatchesForNonAssignedLeads?.find(
                          (item) => Number(item.course_batch_id) === Number(formik.values.new_assigned_batch),
                        ) || null
                      }
                      onChange={(event, value) => {
                        formik.setFieldValue('new_assigned_batch', value?.course_batch_id || '');
                      }}
                      onBlur={() => formik.setFieldTouched('new_assigned_batch', true)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign New Batch"
                          error={formik.touched.new_assigned_batch && Boolean(formik.errors.new_assigned_batch)}
                          helperText={formik.touched.new_assigned_batch && formik.errors.new_assigned_batch}
                        />
                      )}
                    />
                  )}
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Remarks"
                    name="course_inactive_remarks"
                    value={formik.values.course_inactive_remarks}
                    onChange={formik.handleChange}
                    error={formik.touched.course_inactive_remarks && Boolean(formik.errors.course_inactive_remarks)}
                    helperText={formik.touched.course_inactive_remarks && formik.errors.course_inactive_remarks}
                  />
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pt: 2,
                }}
              >
                <Stack direction="row" spacing={1}>
                  <Button size="small" color="secondary" variant="outlined" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>

                  <Button variant="contained" color="secondary" size="small" type="submit" disabled={loading}>
                    {loading ? 'Please wait...' : 'Save'}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default InactiveStudentModal;
